import * as types from '../actiontypes';
import env from '../../util/env';
import {
	serviceURL,
	documentCategoryNames,
	documentSearchTypes,
	relateDocumentsTo,
	documentFilter,
	documentTypes,
	documentSortingOrder
} from '../../util/uiconstants';
import {createApiAction, PostFormBodyReferenceUrl} from '../apiUrls';
import _ from 'lodash';

export const getRelatedDocuments = (
	engagementId,
	targetEngagementId,
	documentId,
	pageSize,
	currentPage = 1,
	documentCategoryName,
	searchText,
	includeApprovals = false,
	includeReviewNotes = false,
	orderBy = null,
	orderByAscending = true
) => {
	let url = env.getBaseURL(serviceURL, engagementId) + `Documents`;

	// Use task filteredEntityType for external and temporary document categories
	if (documentCategoryName === documentCategoryNames.external || documentCategoryName === documentCategoryNames.temporary) {
		url += `?filteredEntityType=${documentSearchTypes.task}`;
	} else {
		url += `?filteredEntityType=${documentSearchTypes.canvasForm}`;
	}

	url +=
		`&documentCategoryName=${documentCategoryName}` +
		`&filteredEntityIdGuid=${documentId}` +
		`&targetEngagementId=${targetEngagementId}` +
		`&includeApprovals=${includeApprovals}` +
		`&includeRelatedTasks=true` +
		`&includeRelatedEntities=true` +
		`&includeReviewNotes=${includeReviewNotes}` +
		`&pageSize=${pageSize}` +
		`&page=${currentPage}` +
		`&searchtype=contains`;

	if (!_.isEmpty(orderBy)) url += `&orderBy=${orderBy}&orderByAscending=${orderByAscending}`;

	if (!_.isEmpty(searchText)) url += `&searchString=${encodeURIComponent(searchText)}`;

	return {
		type: types.GET_ALL_RELATED_DOCUMENTS,
		resolve: true,
		meta: {
			api: {
				url,
				method: 'GET'
			}
		}
	};
};

/**
 * Fetches documents associated across all tasks for a given account, filtered by
 * the provided document category (evidence / prior period). Used by the Document
 * Viewer body (BodyTypeID 312) where the body is aligned to a single account on an
 * Account SWP form.
 */
export const getAccountRelatedDocuments = (
	engagementId,
	accountId,
	pageSize,
	currentPage = 1,
	documentCategoryName,
	includeApprovals = false,
	includeReviewNotes = false,
	orderBy = documentSortingOrder.documentName,
	orderByAscending = true
) => {
	const url =
		env.getBaseURL(serviceURL, engagementId) +
		`Documents` +
		`?filteredEntityType=${documentSearchTypes.account}` +
		`&documentCategoryName=${documentCategoryName}` +
		`&filteredEntityIdInt=${accountId}` +
		`&includeApprovals=${includeApprovals}` +
		`&includeRelatedTasks=true` +
		`&includeRelatedEntities=true` +
		`&includeReviewNotes=${includeReviewNotes}` +
		`&pageSize=${pageSize}` +
		`&page=${currentPage}` +
		`&orderBy=${orderBy}` +
		`&orderByAscending=${orderByAscending}` +
		`&searchtype=contains`;

	return {
		type: types.GET_ALL_RELATED_DOCUMENTS,
		resolve: true,
		meta: {
			api: {
				url,
				method: 'GET'
			}
		}
	};
};

export const getAllRelatedDocuments = (
	engagementId,
	documentId,
	bodyId,
	categoryName,
	filteredEntityType = documentSearchTypes.canvasFormbody,
	filterTaskIds = null
) => {
	categoryName = categoryName ? categoryName : documentCategoryNames.canvasFormBodyEvidence;
	let url =
		env.getBaseURL(serviceURL, engagementId) +
		`Documents` +
		`?filteredEntityType=${filteredEntityType}` +
		`&documentCategoryName=${categoryName}` +
		`${documentId ? `&filteredEntityIdGuid=${documentId}` : ''}` +
		`&includeRelatedTasks=true` +
		`&includeRelatedEntities=true` +
		`&pageSize=-1`;

	if (bodyId) {
		url = `${url}&filteredEntityIdInt=${bodyId}`;
	}

	if (filterTaskIds) {
		url = `${url}&filterTaskId=${filterTaskIds}`;
	}

	return {
		type: types.GET_ALL_RELATED_DOCUMENTS,
		bodyId: bodyId,
		documentId: documentId,
		resolve: true,
		meta: {
			api: {
				url,
				method: 'GET'
			}
		}
	};
};

export const getAllDocumentsToRelate = (
	engagementId,
	pageSize,
	currentPage = 1,
	searchText,
	isOverwriteModal,
	knowledgeFormId,
	documentTypeId,
	documentId,
	categoryName,
	filteredEntityType,
	excludedDocumentTypes,
	onlyEvidenceTypeDocument,
	includeRelatedTask = false,
	excludeDocumentFilter = false
) => {
	let url;
	if (excludeDocumentFilter) {
		url =
			env.getBaseURL(serviceURL, engagementId) +
			`Documents` +
			`?filteredEntityType=${filteredEntityType}` +
			`&documentCategoryName=${categoryName}` +
			`&pageSize=${pageSize}` +
			`&page=${currentPage}` +
			`&ExcludedDocumentIDs=${documentId}` +
			`&ExcludedDocumentTypes=${excludedDocumentTypes}` +
			`&includeRelatedTasks=${includeRelatedTask}`;
	} else {
		url =
			env.getBaseURL(serviceURL, engagementId) +
			`Documents` +
			`?filteredEntityType=${filteredEntityType}` +
			`&documentCategoryName=${categoryName}` +
			`&pageSize=${pageSize}` +
			`&page=${currentPage}` +
			`&includeRelatedTasks=${includeRelatedTask}`;
	}

	if (onlyEvidenceTypeDocument) {
		url = `${url}&documentTypeIdList=${documentTypes.EVIDENCE}`;
	}

	if (!_.isEmpty(searchText)) url += `&searchString=${searchText}`;

	if (isOverwriteModal)
		url += `&filterKnowledgeForm=${knowledgeFormId}&documentTypeIdList=${documentTypes.CANVASFORMEVIDENCE}&filterSharedEvidence=${documentFilter.FilterSharedEvidence}`;

	return {
		type: types.GET_ALL_DOCUMENTS,
		resolve: true,
		meta: {
			api: {
				url,
				method: 'GET'
			}
		}
	};
};

export const createRelatedDocument = (engagementId, documentId, bodyId, relatedDocument, comingFrom, tabname, sectionId) => {
	let url;
	switch (comingFrom) {
		case relateDocumentsTo.canvasForm:
			url = env.getBaseURL(serviceURL, engagementId) + `Forms/` + `${documentId}/` + `References`;
			break;
		case relateDocumentsTo.canvasFormBody:
		case relateDocumentsTo.canvasFormBodyAres:
			url = env.getBaseURL(serviceURL, engagementId) + `Forms/` + `${documentId}/` + `Bodies/` + `${bodyId}/` + `References`;
			break;
	}

	return {
		type: types.CREATE_RELATED_DOCUMENT,
		meta: {
			api: {
				url: url,
				data: relatedDocument,
				method: 'POST'
			},
			tabname: tabname,
			entityUId: bodyId,
			sectionId: sectionId
		}
	};
};

export const createFormBodyReference = (engagementId, documentId, bodyId, referenceDocument, tabname, sectionId) => {
	const url = PostFormBodyReferenceUrl(engagementId, documentId, bodyId);

	return createApiAction(types.CREATE_RELATED_DOCUMENT, url, 'POST', {relatedDocumentId: referenceDocument}, '', {
		tabname: tabname,
		sectionId: sectionId
	});
};

export const deleteRelatedDocument = (engagementId, documentId, bodyId, relatedDocumentId, comingFrom) => {
	let url;
	switch (comingFrom) {
		case relateDocumentsTo.canvasForm:
			url = env.getBaseURL(serviceURL, engagementId) + `Forms/` + `${documentId}/` + `References/` + `${relatedDocumentId}`;
			break;
		case relateDocumentsTo.canvasFormBody:
		case relateDocumentsTo.canvasFormBodyAres:
			url = env.getBaseURL(serviceURL, engagementId) + `Forms/` + `${documentId}/` + `Bodies/` + `${bodyId}/` + `References/` + `${relatedDocumentId}`;
			break;
	}

	return {
		type: types.DELETE_RELATED_DOCUMENT,
		bodyId: bodyId,
		documentId: documentId,
		relatedDocumentId: relatedDocumentId,
		meta: {
			api: {
				url: url,
				method: 'DELETE'
			}
		}
	};
};

export const createTaskDocumentRelation = (engagementId, taskId, payload) => {
	const url = `${env.getBaseURL(serviceURL, engagementId)}Tasks/${taskId}/Documents`;

	return createApiAction(types.RELATE_BODY_GUIDANCE_TO_TASK, url, 'POST', payload, '', {resolve: true});
};

export const createTaskDocuments = (engagementId, payload) => {
	const url = `${env.getBaseURL(serviceURL, engagementId)}TaskDocuments`;

	return createApiAction(types.CREATE_TASK_DOCUMENTS, url, 'POST', payload, '', {resolve: true});
};
